import { createSlice } from "@reduxjs/toolkit";

import { DEFAULT_REPRESENTATIVE_FILTERS } from "views/vitrine-b2b/criar-vitrine/utils/wizard-helpers";

const initialFormData = {
  brand: null,
  priceTable: null,
  priceTables: [],
  initialDate: "",
  finalDate: "",
  untilSoldOut: false,
  name: "",
  paymentCondition: null,
  paymentConditions: [],
  minimumValue: null,
  byLine: false,
  byOrder: false,
  description: "",
  minimumInvoice: null,
  minimumQuantityPerOrder: "",
};

const criarVitrineSlice = createSlice({
  name: "criarVitrine",
  initialState: {
    currentStep: 0,
    maxCompletedStep: 0,
    editMode: false,
    vitrineId: null,
    formData: { ...initialFormData },
    selectedProducts: [],
    representatives: [],
    productFilters: {},
    representativeFilters: { ...DEFAULT_REPRESENTATIVE_FILTERS },
    cachedPaymentByMarca: {},
    cachedPriceTables: null,
  },
  reducers: {
    setCurrentStep: (state, { payload }) => {
      state.currentStep = payload;
    },
    setMaxCompletedStep: (state, { payload }) => {
      state.maxCompletedStep = Math.max(state.maxCompletedStep, payload);
    },
    setEditMode: (state, { payload }) => {
      state.editMode = payload?.editMode ?? false;
      state.vitrineId = payload?.vitrineId ?? null;
    },
    setCachedPaymentOptions: (state, { payload }) => {
      const { idMarca, options } = payload;
      if (idMarca != null) {
        state.cachedPaymentByMarca[idMarca] = options ?? [];
      }
    },
    setCachedPriceTables: (state, { payload }) => {
      state.cachedPriceTables = payload ?? [];
    },
    loadVitrineForEdit: (state, { payload }) => {
      state.editMode = true;
      state.vitrineId = payload.vitrineId;
      state.currentStep = 0;
      state.maxCompletedStep = 2;
      state.formData = payload.formData;
      state.selectedProducts = payload.selectedProducts ?? [];
      state.representatives = payload.representatives ?? [];
      state.productFilters = payload.productFilters ?? {};
      state.representativeFilters =
        payload.representativeFilters ?? { ...DEFAULT_REPRESENTATIVE_FILTERS };
      if (payload.cachedPaymentByMarca) {
        state.cachedPaymentByMarca = {
          ...state.cachedPaymentByMarca,
          ...payload.cachedPaymentByMarca,
        };
      }
      if (payload.cachedPriceTables) {
        state.cachedPriceTables = payload.cachedPriceTables;
      }
    },
    updateFormData: (state, { payload }) => {
      state.formData = { ...state.formData, ...payload };
    },
    setSelectedProducts: (state, { payload }) => {
      state.selectedProducts = payload;
    },
    mergeSelectedProducts: (state, { payload }) => {
      const map = new Map();
      state.selectedProducts.forEach((p) => {
        if (p?.Codigo != null) map.set(p.Codigo, p);
      });
      (payload ?? []).forEach((p) => {
        if (p?.Codigo != null) map.set(p.Codigo, p);
      });
      state.selectedProducts = Array.from(map.values());
    },
    addProduct: (state, { payload }) => {
      const exists = state.selectedProducts.some(
        (p) => p.Codigo === payload.Codigo,
      );
      if (!exists) {
        state.selectedProducts.push(payload);
      }
    },
    removeProduct: (state, { payload }) => {
      state.selectedProducts = state.selectedProducts.filter(
        (p) => p.Codigo !== payload,
      );
    },
    updateProduct: (state, { payload }) => {
      const { codigo, updates } = payload;
      state.selectedProducts = state.selectedProducts.map((item) =>
        item.Codigo === codigo ? { ...item, ...updates } : item,
      );
    },
    setRepresentatives: (state, { payload }) => {
      state.representatives = payload;
    },
    updateRepresentative: (state, { payload }) => {
      state.representatives = payload;
    },
    updateProductFilters: (state, { payload }) => {
      state.productFilters = { ...state.productFilters, ...payload };
    },
    setProductFilters: (state, { payload }) => {
      state.productFilters = payload;
    },
    updateRepresentativeFilters: (state, { payload }) => {
      state.representativeFilters = {
        ...state.representativeFilters,
        ...payload,
      };
    },
    resetVitrineData: (state) => {
      state.currentStep = 0;
      state.maxCompletedStep = 0;
      state.editMode = false;
      state.vitrineId = null;
      state.formData = { ...initialFormData };
      state.selectedProducts = [];
      state.representatives = [];
      state.productFilters = {};
      state.representativeFilters = { ...DEFAULT_REPRESENTATIVE_FILTERS };
      state.cachedPaymentByMarca = {};
      state.cachedPriceTables = null;
    },
  },
});

const { actions, reducer } = criarVitrineSlice;

export const {
  setCurrentStep,
  setMaxCompletedStep,
  setEditMode,
  setCachedPaymentOptions,
  setCachedPriceTables,
  loadVitrineForEdit,
  updateFormData,
  setSelectedProducts,
  mergeSelectedProducts,
  addProduct,
  removeProduct,
  updateProduct,
  setRepresentatives,
  updateRepresentative,
  updateProductFilters,
  setProductFilters,
  updateRepresentativeFilters,
  resetVitrineData,
} = actions;

export default reducer;
